import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { FeedItem } from '../components/FeedItem';
import type { SignalAction } from '../api/bindings/SignalAction';

export function Feed() {
  const qc = useQueryClient();
  const [includeSaved, setIncludeSaved] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['feed', { includeSaved }],
    queryFn: ({ pageParam }) => api.feed({ cursor: pageParam, includeSaved }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const signal = useMutation({
    mutationFn: ({ id, action }: { id: string; action: SignalAction }) =>
      api.signal({ item_id: id, action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }),
  });

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section>
      <header className="page-head">
        <h1>Feed</h1>
        <label className="toggle">
          <input type="checkbox" checked={includeSaved} onChange={(e) => setIncludeSaved(e.target.checked)} />
          Show saved
        </label>
      </header>

      {query.isLoading && <p className="muted">Loading your feed…</p>}
      {query.isError && <p className="error">Couldn’t load the feed.</p>}
      {!query.isLoading && items.length === 0 && (
        <p className="muted empty">
          Nothing here yet. Add a source, or push candidates to the ingest endpoint with an API token.
        </p>
      )}

      <div className="feed-list">
        {items.map((item) => (
          <FeedItem key={item.id} item={item} onSignal={(action) => signal.mutate({ id: item.id, action })} />
        ))}
      </div>

      {query.hasNextPage && (
        <button className="more" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
          {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );
}
